import { Game } from './Game.js';
import { track } from './core/Telemetry.js';
import { challengeIdFromPath } from './net/Share.js';

const canvas = document.getElementById('view');
const boot = document.getElementById('boot');
const bar = document.getElementById('boot-bar');
const stat = document.getElementById('boot-stat');

function fail(message, err) {
  console.error(message, err);
  if (!boot) return;
  boot.innerHTML = `
    <div class="bootlogo" style="color:#ff3355;font-size:28px">SIGNAL FAULT</div>
    <div class="stat" style="max-width:60ch;line-height:2;text-align:center">${message}</div>`;
  boot.classList.remove('fade-out');
}

async function main() {
  if (!window.WebGL2RenderingContext) {
    fail('THIS BROWSER HAS NO WEBGL2. TRY A RECENT CHROME, EDGE, FIREFOX OR SAFARI.');
    return;
  }

  const game = new Game(canvas);
  window.__momentum = game;   // handy for tinkering from the console

  // ?agent=1: the agent deck, a structured tool surface for AI agents
  // (WebMCP-shaped, mirrored on window.__agentDeck). Installed BEFORE the
  // boot await on purpose: boot itself needs frames, a hidden tab gets no
  // rAF, and the deck's worker pump is what supplies frames to background
  // tabs - installed after boot, neither would ever arrive (measured).
  // The module sandboxes the session: no boards, no records, no telemetry.
  if (new URLSearchParams(location.search).has('agent')) {
    import('./ai/AgentDeck.js').then(({ installAgentDeck }) => installAgentDeck(game));
  }

  try {
    await game.boot((pct, label) => {
      if (bar) bar.style.width = `${pct}%`;
      if (stat) stat.textContent = label;
    });
  } catch (err) {
    fail('THE ENGINE FAILED TO START. CHECK THE CONSOLE FOR DETAILS.', err);
    return;
  }

  // A reclaimed GPU context otherwise leaves a black canvas under a live
  // HUD with no way out but a reload the player has to think of themselves.
  canvas.addEventListener('webglcontextlost', (e) => {
    e.preventDefault();
    document.getElementById('ctxlost')?.classList.remove('hidden');
  });
  document.getElementById('ctxlost-reload')?.addEventListener('click', () => location.reload());

  // The PWA layer: offline shell + installability. Skipped under
  // automation so the test harness never fights a cache, and skipped in
  // dev so vite's module graph stays live.
  if ('serviceWorker' in navigator && !navigator.webdriver && import.meta.env.PROD) {
    navigator.serviceWorker.register('/sw.js').catch((err) => {
      console.info('[sw] registration failed:', err?.message ?? err);
    });
  }

  boot?.classList.add('fade-out');
  setTimeout(() => boot?.classList.add('hidden'), 600);

  // /r/<id>: a ghost challenge link. The Worker served this same shell
  // with the run's social tags; the game opens the challenge screen.
  const challengeId = challengeIdFromPath(location.pathname);
  if (challengeId) game.openChallenge(challengeId);

  // Level editor entry. Loaded on demand as its own chunk so players never
  // download editor bytes, and resolved through a glob so the game still
  // builds byte-identically if src/editor/ is deleted (the glob is empty).
  const editorChunks = import.meta.glob('./editor/EditorMode.js');
  const loadEditor = editorChunks['./editor/EditorMode.js'];
  if (loadEditor) {
    const enterEditor = async () => (await loadEditor()).enterEditor(game);
    window.__momentumEditorEnter = enterEditor;
    // Public since 1.5.0: the editor is harmless by construction (playtests
    // never write records; submissions are reviewed before they ship). The
    // button skips touch-only devices: the tool needs a mouse to look and a
    // keyboard to fly, and a menu button that opens a dead end is worse
    // than none. #editor still opens it anywhere.
    const editorBtn = document.getElementById('go-editor');
    if (editorBtn && !game.input.touch) {
      editorBtn.classList.remove('hidden');
      editorBtn.addEventListener('click', () => enterEditor());
    }
    // #editor opens the editor; #editor=sub:<id> additionally loads that
    // submission from the inbox (the admin portal's REVIEW deep link;
    // useless without the admin key, and it fails as a polite toast).
    const hash = decodeURIComponent(location.hash)
      .match(/^#editor(?:=(sub:[0-9]{1,16}-[0-9a-f]{8}))?$/);
    if (hash) {
      enterEditor().then((ed) => {
        if (hash[1]) {
          // Came from the admin portal's REVIEW link: EXIT goes back there.
          ed._reviewReturn = true;
          ed.loadSubmission(hash[1]);
        }
      });
    }
  }

  // Install-base column: the browser fires this once, at the moment of
  // Add to Home Screen. (Sessions running installed carry a 'pwa' flag in
  // session_start instead.)
  window.addEventListener('appinstalled', () => track('pwa_install'));

  // Audio contexts stay suspended until a real gesture; catch the first one
  // anywhere on the page rather than making the player hunt for a button.
  const unlock = () => game.audio.unlock();
  window.addEventListener('pointerdown', unlock, { once: true });
  window.addEventListener('keydown', unlock, { once: true });
}

main();
